import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { selectIncompleteUploadedFileIds } from '@/uppy-file-upload/store/selectors'
import { UserFilesPollingActions } from '@/user_files/polling/store/slice'
import { selectCurrentFolderId } from '@/user_files/folders/store/selectors'
import { UserFilesActions } from './slice'
import { selectCurrentFolderProcessingFileIds } from './selectors'
import { getFolderFiles } from '../fetch/get-folder-files'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { ApiError } from '@/util/errors/api-error'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { getFolderAncestorIds } from '@/user_files/folders/utils/get-folder-ancestor-ids'
import { type DriveListSort, driveListSortsEqual, kDefaultDriveListSort } from '@/user_files/sorting'
import { isTerminalProcessingStatus } from '@/user_files/utils/processing-status'
import { isAbortError } from '@/util/errors/is-abort-error'

export const userFilesListenerMiddleware = createListenerMiddleware()
const startListener = userFilesListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()
/**
 * One in-flight file load per folder, with the sort it was issued under.
 *
 * A second request for the same folder is either a duplicate (same sort — coalesced into one
 * queued refresh) or a supersession (different sort — the older request is aborted). Without the
 * abort, a slow default-sort load could resolve after a sort change and overwrite the cache with
 * the order the user just navigated away from.
 */
const inFlightFolderFileLoads = new Map<string, { sort: DriveListSort; controller: AbortController }>()
// Folder ids only, never the sort they were queued under: by the time the replay runs the sort may
// have moved on, and dispatching without one lets the fallback resolve the order in force then.
const queuedFolderFileRefreshes = new Set<string>()

function isMissingOrDeniedError(error: unknown): error is ApiError {
  return error instanceof ApiError && (error.status === 403 || error.status === 404)
}

// ============================================================================
// Fetch Files for Folder
// ============================================================================

startListener({
  actionCreator: UserFilesActions.fetchFilesForFolder,
  effect: async (action, listenerApi) => {
    const { folderId } = action.payload
    const inFlight = inFlightFolderFileLoads.get(folderId)
    // A caller with no sorting UI (the file picker, an upload or export refresh) means "reload this
    // folder", not "reorder it", so it inherits an order rather than choosing one. An in-flight load
    // is a more current answer than the cache: while a sort change is being fetched the cache still
    // describes the sort being replaced, and inheriting *that* would make this refresh look like a
    // different request and supersede the sort the user just asked for.
    const sort = action.payload.sort ?? inFlight?.sort ?? listenerApi.getState().userFilesState.fileListSortByFolderId[folderId] ?? kDefaultDriveListSort

    if (inFlight) {
      if (driveListSortsEqual(inFlight.sort, sort)) {
        queuedFolderFileRefreshes.add(folderId)
        return
      }
      inFlight.controller.abort()
    }

    const controller = new AbortController()
    inFlightFolderFileLoads.set(folderId, { sort, controller })
    listenerApi.dispatch(UserFilesActions.fetchFilesStarted({ folderId }))

    try {
      const response = await getFolderFiles(folderId, {
        sort,
        signal: controller.signal,
        statuses: [UserFileProcessingStatus.Pending, UserFileProcessingStatus.Processing, UserFileProcessingStatus.Completed, UserFileProcessingStatus.Failed],
      })
      // `abort()` only rejects a request still in flight. If the last page resolved just before a
      // sort change, this continuation is already queued and would land the old order on top of the
      // new one. The signal is the authority on whether this request still owns the state.
      if (controller.signal.aborted) return

      listenerApi.dispatch(
        UserFilesActions.fetchFilesSucceeded({
          folderId,
          // Extracted children can still be pending when their parent email finishes. Keep them
          // visible and pollable without adding ordinary browser uploads before Uppy owns them.
          files: response.files.filter((file) => file.processing_status !== UserFileProcessingStatus.Pending || !!file.extracted_from_file_id),
          sort,
        })
      )
    } catch (error) {
      // A superseded request is not a failure, and the request that replaced it owns the state.
      if (isAbortError(error)) return

      console.error('Failed to fetch folder files:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch files'
      const statusCode = error instanceof ApiError ? error.status : undefined
      if (isMissingOrDeniedError(error)) {
        listenerApi.dispatch(UserFilesActions.clearFolderFiles({ folderId }))
        if (selectCurrentFolderId(listenerApi.getState()) === folderId) {
          listenerApi.dispatch(UserFoldersActions.urlInitializationFailed({ folderId, error: errorMessage, statusCode: error.status }))
        }
      }
      listenerApi.dispatch(UserFilesActions.fetchFilesFailed({ folderId, error: errorMessage, statusCode }))
    } finally {
      // Only tidy up if this request is still the current one for the folder; an aborted request
      // must not clear the entry or replay the queue on behalf of the request that replaced it.
      if (inFlightFolderFileLoads.get(folderId)?.controller === controller) {
        inFlightFolderFileLoads.delete(folderId)

        // Names this request's own sort rather than letting the replay inherit: the queue is only
        // drained by a request that was never superseded, so its sort is the most recent one asked
        // for — and on the failure path the cache still describes the sort this one was replacing.
        if (queuedFolderFileRefreshes.delete(folderId)) {
          listenerApi.dispatch(UserFilesActions.fetchFilesForFolder({ folderId, sort }))
        }
      }
    }
  },
})

startListener({
  actionCreator: UserFilesPollingActions.pollBatchReceived,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState()
    const currentFolderId = selectCurrentFolderId(state)

    const currentFolderFileIds = new Set<string>(currentFolderId ? state.userFilesState.filesByFolderId[currentFolderId] ?? [] : [])
    const currentFolderProcessingIds = new Set(selectCurrentFolderProcessingFileIds(state))
    const drawerFileIds = new Set(selectIncompleteUploadedFileIds(state))
    const isCurrentFolderLoaded = currentFolderId ? state.userFilesState.fileListRequestsByFolderId[currentFolderId]?.status === 'success' : false

    const returnedRecords = Object.values(action.payload.files).map((fileData) => fileData.record)

    // Update records that belong to a loaded folder.
    const recordsToUpdate = returnedRecords.filter((record) => state.userFilesState.filesById[record.id])
    if (recordsToUpdate.length > 0) {
      listenerApi.dispatch(UserFilesActions.upsertUserFiles(recordsToUpdate))
    }

    const returnedIds = new Set(Object.keys(action.payload.files))
    const hasMissingCurrentFolderFiles =
      !!currentFolderId && action.payload.requestedIds.some((requestedId) => currentFolderProcessingIds.has(requestedId) && !returnedIds.has(requestedId))

    const shouldRefreshCurrentFolder = returnedRecords.some((record) => {
      if (!currentFolderId) return false
      if (!drawerFileIds.has(record.id)) return false
      if (record.processing_status === UserFileProcessingStatus.Pending) return false
      if (record.folder_id !== currentFolderId) return false
      if (!isCurrentFolderLoaded) return false
      if (currentFolderFileIds.has(record.id)) return false
      return true
    })

    const foldersToRefresh = new Set(
      returnedRecords
        .filter((record) => {
          const previous = state.userFilesState.filesById[record.id]
          return (
            previous !== undefined &&
            !isTerminalProcessingStatus(previous.processing_status) &&
            isTerminalProcessingStatus(record.processing_status) &&
            (record.detected_extension === 'eml' || record.detected_extension === 'msg') &&
            state.userFilesState.filesByFolderId[record.folder_id]?.includes(record.id)
          )
        })
        .map((record) => record.folder_id)
    )

    if (currentFolderId && (hasMissingCurrentFolderFiles || shouldRefreshCurrentFolder)) {
      foldersToRefresh.add(currentFolderId)
    }
    foldersToRefresh.forEach((folderId) => listenerApi.dispatch(UserFilesActions.fetchFilesForFolder({ folderId })))

    // A folder's `file_count` covers its whole subtree, so a file finishing changes the count on
    // every ancestor's row. Those counts live in the ancestors' children listings, which the refresh
    // above does not touch — mark them stale so the next visit refetches.
    //
    // Keyed on each record's own `folder_id` rather than the open folder, because the count can
    // change in a folder the user is not looking at: a folder upload creates the child row reading
    // `0 files` (the create endpoint has no count to give yet) and only then fills it, and an upload
    // keeps landing after the user has browsed away.
    //
    // A terminal status fires once per file: every polling source drops a file id as soon as it
    // reaches one, so the record cannot come back on a later tick and re-invalidate.
    const foldersWithChangedCounts = new Set(
      returnedRecords.filter((record) => isTerminalProcessingStatus(record.processing_status)).map((record) => record.folder_id)
    )
    // A file that disappeared from the open folder changes counts too, and returns no record to key on.
    if (currentFolderId && hasMissingCurrentFolderFiles) {
      foldersWithChangedCounts.add(currentFolderId)
    }

    const staleCountFolderIds = new Set(
      // `getFolderAncestorIds` returns root → folder inclusive; the slice drops the folder itself,
      // because its own row lives in its parent's listing, not in its own.
      [...foldersWithChangedCounts].flatMap((folderId) => getFolderAncestorIds(state.userFoldersState.folders, folderId).slice(0, -1))
    )
    staleCountFolderIds.forEach((folderId) => listenerApi.dispatch(UserFoldersActions.invalidateFolderChildren({ folderId })))
  },
})
